import { useMemo } from 'react';

import { Card } from 'antd';
import ReactECharts from 'echarts-for-react';

import { formatMetricValue } from '@/shared/lib/utils/number';
import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { GPU_CHART_COLORS } from '@/features/monitoring/api/constants';
import { useVramUtilization } from '@/features/monitoring/model';

import { createGpuLineChartOption } from '@/widgets/monitoring/shared';

import styles from './VramUtilizationMetric.module.css';

type VramUtilizationMetricProps = {
  selectedWorker: string;
};

const Content = ({ selectedWorker }: VramUtilizationMetricProps) => {
  const { data: vramData, isLoading } = useVramUtilization(selectedWorker || '', !!selectedWorker);

  const chartOption = useMemo(() => {
    if (!vramData || vramData.length === 0) {
      return {};
    }
    return createGpuLineChartOption(vramData, GPU_CHART_COLORS);
  }, [vramData]);

  if (!selectedWorker) {
    return (
      <Card title="GPU VRAM Utilization" className={styles.card}>
        <div className={styles.noData}>Worker를 선택해주세요</div>
      </Card>
    );
  }

  return (
    <Card title="GPU VRAM Utilization" className={styles.card}>
      {isLoading ? (
        <LoadingState message="VRAM 데이터 로딩 중..." compact />
      ) : vramData && vramData.length > 0 ? (
        <div>
          <ReactECharts option={chartOption} className={styles.chart} opts={{ renderer: 'canvas' }} notMerge={true} />
          <div className={styles.legendTable}>
            <div className={styles.legendTableHeader}>
              <div className={styles.legendTableCell}>Name</div>
              <div className={styles.legendTableCell}>Mean</div>
              <div className={styles.legendTableCell}>Last*</div>
              <div className={styles.legendTableCell}>Max</div>
            </div>
            {vramData.map((metric, index) => (
              <div key={`${metric.displayName}-${index}`} className={styles.legendTableRow}>
                <div className={styles.legendTableCell}>
                  <span
                    className={styles.legendColor}
                    style={{ backgroundColor: GPU_CHART_COLORS[index % GPU_CHART_COLORS.length] }}
                  />
                  {metric.displayName}
                </div>
                <div className={styles.legendTableCell}>{formatMetricValue(metric.mean)}%</div>
                <div className={styles.legendTableCell}>{formatMetricValue(metric.last)}%</div>
                <div className={styles.legendTableCell}>{formatMetricValue(metric.max)}%</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.noData}>선택한 Worker에 VRAM 데이터가 없습니다.</div>
      )}
    </Card>
  );
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return (
    <Card title="GPU VRAM Utilization" className={styles.card}>
      <ErrorFallback title={title} description={description} {...props} />
    </Card>
  );
};

export const VramUtilizationMetric = { Content, Error };
